"use client";

import { useCallback, useState } from "react";

export type ToastType = "error" | "success" | "info" | "warning";

export interface Toast {
	id: string;
	type: ToastType;
	message: string;
	duration?: number;
}

interface ToastConfig {
	defaultDuration?: number;
	autoClose?: boolean;
}

export function useToast(config: ToastConfig = {}) {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	const addToast = useCallback(
		(type: ToastType, message: string, duration?: number) => {
			const id = Math.random().toString(36).substr(2, 9);
			const finalDuration = duration ?? config.defaultDuration ?? 5000;
			const newToast: Toast = { id, type, message, duration: finalDuration };

			setToasts((prev) => [...prev, newToast]);

			// Auto remove if enabled
			if (config.autoClose !== false && finalDuration > 0) {
				setTimeout(() => {
					removeToast(id);
				}, finalDuration);
			}

			return id;
		},
		[config, removeToast]
	);

	const success = useCallback(
		(message: string, duration?: number) => {
			return addToast("success", message, duration);
		},
		[addToast]
	);

	const error = useCallback(
		(message: string, duration?: number) => {
			return addToast("error", message, duration);
		},
		[addToast]
	);

	const info = useCallback(
		(message: string, duration?: number) => {
			return addToast("info", message, duration);
		},
		[addToast]
	);

	const warning = useCallback(
		(message: string, duration?: number) => {
			return addToast("warning", message, duration);
		},
		[addToast]
	);

	const clearAll = useCallback(() => {
		setToasts([]);
	}, []);

	return {
		toasts,
		addToast,
		removeToast,
		success,
		error,
		info,
		warning,
		clearAll,
	};
}
